import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Paperclip } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { FloorplanPanel } from '../../components/floorplan-editor';
import { useToast } from '../../components/toast';
import { WorkOrderStatusBadge } from '../../components/work-order-badges';
import { Badge, Card, CardHeader, DescriptionList, ErrorState, PageHeader, PageLoader } from '../../components/ui';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { workOrderHref } from '../../lib/desk';
import { eventPlaceLabel, INLINE_ATTACHMENT_TYPES, TEAM_LABEL, type EventRequest, type EventTicket } from '../../lib/events';
import { formatBytes } from '../../lib/facilities';
import type { Floorplan } from '../../lib/floorplan';
import { formatDateTime } from '../../lib/format';
import type { HelpdeskService } from '../../lib/types';

function TicketLink({ ticket }: { ticket: EventTicket }) {
  return (
    <Link to={workOrderHref(ticket.service, ticket.id)} className="inline-flex flex-wrap items-center gap-2 text-sm">
      <span className="font-medium text-brand-800 hover:underline">#{ticket.number}</span>
      <WorkOrderStatusBadge status={ticket.status} />
    </Link>
  );
}

/** What one team was asked for, and the work order it got. */
function NeedsCard({ title, needs, notes, neededBy, ticket }: { title: string; needs: string[]; notes: string | null; neededBy: string | null; ticket: EventTicket | null }) {
  if (needs.length === 0) return null;
  return (
    <Card>
      <CardHeader title={title} actions={ticket && <TicketLink ticket={ticket} />} />
      <div className="space-y-4 p-5">
        <div className="flex flex-wrap gap-2">
          {needs.map((need) => (
            <Badge key={need} tone="brand">
              {need}
            </Badge>
          ))}
        </div>
        <DescriptionList
          columns={2}
          items={[
            { label: 'Need setup by', value: neededBy && formatDateTime(neededBy) },
            { label: 'Assigned to', value: ticket ? (ticket.assignedTo?.name ?? 'Not assigned yet') : null },
          ]}
        />
        {notes && (
          <div>
            <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">Additional notes</p>
            <p className="mt-1 text-sm whitespace-pre-wrap text-slate-800">{notes}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

/** One event request: the event, what each team was asked for, the work orders it opened, and its attachment. */
export function EventRequestDetailPage() {
  const { id } = useParams();
  const query = useQuery({ queryKey: ['event-request', id], queryFn: () => api.get<EventRequest>(`/event-requests/${id}`) });
  const toast = useToast();
  const queryClient = useQueryClient();
  const saveFloorplan = useMutation({
    mutationFn: (floorplan: Floorplan | null) => api.put<EventRequest>(`/event-requests/${id}/floorplan`, { floorplan }),
    onSuccess: (saved, floorplan) => {
      queryClient.setQueryData(['event-request', id], saved);
      void queryClient.invalidateQueries({ queryKey: ['work-order'] });
      toast.success(floorplan ? 'Floorplan saved' : 'Floorplan removed');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const event = query.data;
  const ticketFor = (service: HelpdeskService) => event.workOrders.find((ticket) => ticket.service === service) ?? null;
  const attachment = event.attachment;

  return (
    <>
      <PageHeader
        back={{ to: '/events', label: 'Event requests' }}
        title={event.title}
        description={`${event.eventType} · ${formatDateTime(event.startsAt)} · ${eventPlaceLabel(event)}`}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Event" />
            <div className="space-y-5 p-5">
              <DescriptionList
                columns={2}
                items={[
                  { label: 'Date & time', value: formatDateTime(event.startsAt) },
                  { label: 'Where', value: eventPlaceLabel(event) },
                  { label: 'Setup time', value: formatDateTime(event.setupAt) },
                  { label: 'Breakdown time', value: formatDateTime(event.breakdownAt) },
                  { label: 'Event type', value: event.eventType },
                  { label: 'Attendees', value: event.attendees.toLocaleString() },
                  { label: 'Requested by', value: event.requesterEmail ? `${event.requesterName} · ${event.requesterEmail}` : event.requesterName },
                  { label: 'Sent', value: formatDateTime(event.createdAt) },
                ]}
              />
              {event.description && (
                <div>
                  <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">Event description</p>
                  <p className="mt-1 text-sm whitespace-pre-wrap text-slate-800">{event.description}</p>
                </div>
              )}
            </div>
          </Card>
          <Card>
            <CardHeader title="Floorplan" description="How the space should be set up" />
            <div className="p-5">
              <FloorplanPanel
                plan={event.floorplan}
                canEdit={event.permissions.editFloorplan}
                saving={saveFloorplan.isPending}
                onChange={(floorplan) => saveFloorplan.mutateAsync(floorplan)}
                name={event.title}
                meta={
                  event.floorplanUpdatedAt
                    ? `Last changed ${formatDateTime(event.floorplanUpdatedAt)}${event.floorplanUpdatedByName ? ` by ${event.floorplanUpdatedByName}` : ''}`
                    : undefined
                }
                emptyText={
                  event.permissions.editFloorplan ? 'No floorplan yet. Draw one so each team sets up the space the same way.' : 'No floorplan was drawn for this event.'
                }
              />
            </div>
          </Card>
          <NeedsCard title="Tech needs" needs={event.techNeeds} notes={event.techNotes} neededBy={event.techNeededBy} ticket={ticketFor('IT')} />
          <NeedsCard title="Maintenance needs" needs={event.facilitiesNeeds} notes={event.facilitiesNotes} neededBy={event.facilitiesNeededBy} ticket={ticketFor('FACILITIES')} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Work orders" description="Follow each team’s progress." />
            <ul className="divide-y divide-slate-100">
              {event.workOrders.map((ticket) => (
                <li key={ticket.id} className="space-y-1 px-5 py-3">
                  <p className="text-xs font-medium text-slate-500">{TEAM_LABEL[ticket.service]}</p>
                  <TicketLink ticket={ticket} />
                  <p className="text-xs text-slate-500">{ticket.assignedTo ? `Assigned to ${ticket.assignedTo.name}` : 'Not assigned yet'}</p>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <CardHeader title="Attachment" />
            <div className="p-5 text-sm">
              {attachment ? (
                <a
                  href={apiUrl(`/event-requests/${event.id}/attachment`, { inline: INLINE_ATTACHMENT_TYPES.has(attachment.contentType) ? true : undefined })}
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 hover:bg-slate-50"
                >
                  <Paperclip className="size-4 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-brand-800">{attachment.fileName}</span>
                    <span className="text-xs text-slate-500">{formatBytes(attachment.sizeBytes)}</span>
                  </span>
                  <Download className="size-4 shrink-0 text-slate-400" />
                </a>
              ) : (
                <p className="text-slate-500">No attachment.</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
